import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CaretLeft as ChevronLeft, CaretRight as ChevronRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { RelayLoader } from "../components/RelayLoader";
import { mask, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
  red: "var(--pr-red)",
  green: "var(--pr-green)",
  teal: "var(--pr-teal)",
  tealSoft: "var(--pr-tint-teal)",
};

const fmt = (n) => (n == null ? "n/a" : mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`));
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const shortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  const mm = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${mm[m - 1]} ${d}`;
};
const fullDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

const DAY_META = {
  day_off: { emoji: "😌", label: "Day off" },
  sick: { emoji: "🤒", label: "Sick day" },
  pto: { emoji: "🌴", label: "PTO day" },
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function TimeOffPage() {
  usePrivacy();  // re-render when Creator Privacy Mode flips
  const [view, setView] = useState("loading"); // loading | signed_out | not_member | ready
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null); // date iso
  const [pendingType, setPendingType] = useState(null); // sick | pto awaiting paid choice
  const [busy, setBusy] = useState(false);
  const [weekPending, setWeekPending] = useState(false);
  const [editVal, setEditVal] = useState("");
  const [showEarnings, setShowEarnings] = useState(false);
  const [showWeek, setShowWeek] = useState(false);
  const [showBankHelp, setShowBankHelp] = useState(false);

  const load = async (month) => {
    try {
      const q = month ? `?month=${month}` : "";
      const res = await fetch(`${API}/api/caddy/calendar${q}`, { credentials: "include" });
      if (res.status === 401) { setView("signed_out"); return null; }
      if (res.status === 403) { setView("not_member"); return null; }
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Couldn't load the calendar"); return null; }
      setData(json);
      setView("ready");
      return json;
    } catch {
      if (data) toast.error("Couldn't refresh, check your connection and try again");
      else setView("error");
      return null;
    }
  };

  useEffect(() => { load(); }, []);

  // On mobile the day panel sits under the calendar, bring it into view.
  useEffect(() => {
    if (!selected) return;
    const id = requestAnimationFrame(() => {
      document.querySelector('[data-testid="day-panel"]')
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [selected]);

  const setDayEarnings = async (amt) => {
    if (!selected || busy) return;
    const day = selected;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/log`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: day, amount: amt, mode: "replace" }),
      });
      const json = await res.json();
      if (!res.ok) {
        const d = json.detail;
        toast.error(typeof d === "string" ? d : d?.message || "Couldn't update that day, try again");
        return;
      }
      setEditVal("");
      await load(data.month);
      const synced = json.logged?.synced_to_sheet;
      if (amt === 0) {
        toast.success(`Earnings cleared for ${shortDate(day)}${synced ? ", sheet updated ✓" : ""}`);
      } else {
        toast.success(`${shortDate(day)} set to ${fmt(amt)}${synced ? ", sheet updated ✓" : ""}`);
      }
      if (!synced && json.sheet_url) {
        toast.message("Saved in the app", { description: json.sheet_error || "We couldn't reach your sheet just now." });
      }
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  const tagDay = async (type, paidFromBank = false) => {
    if (!selected || busy) return;
    const hadEarned = (data?.days || []).find((d) => d.date === selected)?.earned || 0;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/day-type`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selected, type, paid_from_bank: paidFromBank }),
      });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Couldn't tag that day, try again"); return; }
      setPendingType(null);
      await load(data.month);
      if (type === "none") {
        toast.success("Tag cleared");
      } else if (paidFromBank) {
        toast.success(`${DAY_META[type].label} covered, ${fmt(json.day_type.amount_covered)} from your PTO bank 🌴`);
      } else {
        toast.success(`${DAY_META[type].label} logged ✓`);
      }
      if (type !== "none" && hadEarned > 0) {
        toast(`This day still has ${fmt(hadEarned)} of earnings logged`, {
          id: "clear-earnings-prompt",
          duration: 15000,
          description: "Want to zero it out too, in the app and your sheet?",
          action: { label: `Clear the ${fmt(hadEarned)}`, onClick: () => setDayEarnings(0) },
          cancel: { label: "Keep it" },
        });
      }
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleWeekOff = async (on, paidFromBank = false) => {
    if (!selected || busy) return;
    setBusy(true);
    setWeekPending(false);
    try {
      const res = await fetch(`${API}/api/caddy/week-off`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selected, on, paid_from_bank: paidFromBank }),
      });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Couldn't update, try again"); return; }
      await load(data.month);
      if (!on) toast.success("PTO week unmarked");
      else if (paidFromBank) toast.success(`Paid PTO week 🌴, ${fmt(json.amount_covered)} drawn from your bank${json.synced_to_sheet ? " and synced to your sheet" : ""}`);
      else toast.success("Whole week marked as unpaid PTO 🌴. Your streak is safe");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="timeoff-loading">
        <div className="text-center">
          <RelayLoader label="Loading your calendar…" />
        </div>
      </div>
    );
  }

  if (view === "signed_out" || view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm w-full text-center" data-testid="timeoff-gate">
          <h1 className="text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Time Off & PTO
          </h1>
          <p className="text-sm leading-relaxed mb-8" style={{ color: COLOR.ink2 }}>
            {view === "signed_out"
              ? "Sign in to plan days off, sick days, and PTO weeks, without losing your streak."
              : "Time Off tracking comes with your Payday Relay Season Pass."}
          </p>
          <Link
            to={view === "signed_out" ? "/login?next=/app/timeoff" : "/start/free-sprint"}
            className="inline-block px-8 py-3.5 rounded-full font-bold text-base"
            style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
            data-testid="timeoff-gate-cta"
          >
            {view === "signed_out" ? "Sign in with email →" : "Start Free Sprint →"}
          </Link>
        </div>
      </div>
    );
  }

  const [year, monthNum] = data.month.split("-").map(Number);
  const firstWeekday = new Date(year, monthNum - 1, 1).getDay(); // 0 = Sun
  const weekOffSet = new Set(data.week_offs || []);
  const dayByDate = Object.fromEntries((data.days || []).map((d) => [d.date, d]));
  const sel = selected ? dayByDate[selected] : null;
  const selIsFuture = selected && selected > data.today;
  const selWeekOff = sel ? weekOffSet.has(sel.week_start) : false;
  const canAffordPaid = data.daily_target != null && (data.bank?.balance ?? 0) + 0.004 >= data.daily_target;
  const selWeekDetail = sel ? (data.week_off_details || {})[sel.week_start] : null;
  const daysPerWeek = data.days_per_week || 5;
  const weekCost = data.daily_target != null ? Math.round(data.daily_target * daysPerWeek * 100) / 100 : null;
  const canAffordWeek = weekCost != null && (data.bank?.balance ?? 0) + 0.004 >= weekCost;
  const t = data.tallies || {};
  const anyTallies = (t.pto_days || 0) + (t.sick_days || 0) + (t.days_off || 0) + (t.pto_weeks || 0) > 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="timeoff-page">
      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="timeoff" sprint={data?.entitlements?.mode === "sprint"} />
        <main className="px-5 py-6 lg:px-0">
          <div className="max-w-2xl mx-auto lg:mx-0">
            <Link to="/app" className="inline-flex items-center gap-1.5 text-sm font-semibold mb-5" style={{ color: COLOR.ink2 }} data-testid="timeoff-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Payday Relay
            </Link>

            <h1 className="text-2xl lg:text-3xl mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Time Off & PTO
            </h1>
            <p className="text-sm leading-relaxed mb-5" style={{ color: COLOR.ink2 }}>
              Tag days off, sick days, and PTO, past or future. Rest is part of the race plan, not a break from it.
            </p>

            {/* PTO Bank */}
            <div className="rounded-2xl p-5 lg:p-6 mb-5 pr-card-tint" style={{ background: "var(--pr-tint-teal)" }} data-testid="pto-bank-card">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: COLOR.teal }}>
                🌴 PTO bank{data.banks?.source === "sheet" ? ", live from your sheet" : ""}
              </p>
              <p className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.teal }} data-testid="pto-bank-balance">
                {fmt(data.bank?.balance)} <span className="text-sm font-normal" style={{ color: COLOR.ink2 }}>remaining</span>
              </p>
              {data.banks?.source === "sheet" ? (
                <>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    <div className="rounded-xl py-2 px-1" style={{ background: "var(--pr-card)" }}>
                      <p className="text-[10px] font-bold uppercase" style={{ color: COLOR.muted }}>Accrued</p>
                      <p className="text-sm font-bold" style={{ color: COLOR.green }} data-testid="pto-year-accrued">{fmt(data.banks.year?.pto_accrued)}</p>
                    </div>
                    <div className="rounded-xl py-2 px-1" style={{ background: "var(--pr-card)" }}>
                      <p className="text-[10px] font-bold uppercase" style={{ color: COLOR.muted }}>Drawn</p>
                      <p className="text-sm font-bold" style={{ color: COLOR.ink }} data-testid="pto-year-drawn">{fmt(data.banks.year?.pto_drawn)}</p>
                    </div>
                    <div className="rounded-xl py-2 px-1" style={{ background: "var(--pr-card)" }}>
                      <p className="text-[10px] font-bold uppercase" style={{ color: COLOR.muted }}>Remaining</p>
                      <p className="text-sm font-bold" style={{ color: COLOR.teal }} data-testid="pto-year-remaining">{fmt(data.banks.year?.pto_remaining)}</p>
                    </div>
                  </div>
                  <p className="text-xs mt-2.5 leading-relaxed" style={{ color: COLOR.ink2 }} data-testid="pto-month-line">
                    {data.banks.month?.name === "Relay Challenge"
                      ? "This Sprint"
                      : `This month (${data.banks.month?.name})`}: <b style={{ color: COLOR.green }} data-testid="pto-month-accrued">{fmt(data.banks.month?.pto_accrued)}</b> accrued · <b style={{ color: COLOR.ink }} data-testid="pto-month-used">{fmt(data.banks.month?.pto_used)}</b> used
                  </p>
                  {showBankHelp ? (
                    <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: COLOR.muted }} data-testid="pto-bank-help">
                      <b>Accrued</b> = what you&rsquo;ve built up · <b>Drawn</b> = what you&rsquo;ve spent on paid days · <b>Remaining</b> = what&rsquo;s left.
                      Your sheet keeps the score, paid days here write into it{data.daily_target != null ? <> ({fmt(data.daily_target)} per day)</> : null}.
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowBankHelp(true)}
                      className="text-[11px] font-semibold underline mt-1.5"
                      style={{ color: COLOR.muted }}
                      data-testid="pto-bank-help-toggle"
                    >
                      What these numbers mean
                    </button>
                  )}
                </>
              ) : (
                <p className="text-xs mt-1 leading-relaxed" style={{ color: COLOR.ink2 }}>
                  {fmt(data.bank?.deposited)} set aside · {fmt(data.bank?.withdrawn)} used for paid days.
                  Your bank grows from the PTO you <b style={{ color: COLOR.ink }}>actually set aside each relay</b>, log those on your dashboard after each payout.
                  {data.daily_target != null && <> A paid day off draws your daily target ({fmt(data.daily_target)}).</>}
                </p>
              )}
            </div>

            {/* Tallies */}
            {anyTallies && (
              <div className="rounded-2xl p-5 lg:p-6 pr-card mb-5" data-testid="timeoff-tallies">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: COLOR.muted }}>This race so far</p>
                <p className="text-sm" style={{ color: COLOR.ink }}>
                  🌴 <b>{t.pto_days || 0}</b> PTO day{t.pto_days === 1 ? "" : "s"} · 🤒 <b>{t.sick_days || 0}</b> sick day{t.sick_days === 1 ? "" : "s"} · 😌 <b>{t.days_off || 0}</b> day{t.days_off === 1 ? "" : "s"} off · <b>{t.pto_weeks || 0}</b> PTO week{t.pto_weeks === 1 ? "" : "s"}
                </p>
                {t.covered_total > 0 && (
                  <p className="text-xs mt-1" style={{ color: COLOR.teal }}>{fmt(t.covered_total)} of paid time off covered from your bank</p>
                )}
              </div>
            )}

            {/* Month nav */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => load(shiftMonth(data.month, -1))}
                className="p-2 rounded-full hover:bg-black/5"
                aria-label="Previous month"
                data-testid="cal-prev"
              >
                <ChevronLeft className="w-5 h-5" style={{ color: COLOR.ink }} />
              </button>
              <p className="font-bold text-lg" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="cal-month-label">
                {MONTHS[monthNum - 1]} {year}
              </p>
              <button
                onClick={() => load(shiftMonth(data.month, 1))}
                className="p-2 rounded-full hover:bg-black/5"
                aria-label="Next month"
                data-testid="cal-next"
              >
                <ChevronRight className="w-5 h-5" style={{ color: COLOR.ink }} />
              </button>
            </div>

            {/* Calendar grid */}
            <div className="rounded-2xl p-5 lg:p-6 pr-card mb-2">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                  <p key={d} className="text-center text-[10px] font-bold uppercase" style={{ color: COLOR.muted }}>{d}</p>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
                {Array.from({ length: firstWeekday }).map((_, i) => <div key={`b${i}`} />)}
                {(data.days || []).map((d) => {
                  const isToday = d.date === data.today;
                  const isSel = d.date === selected;
                  const inWeekOff = weekOffSet.has(d.week_start);
                  const meta = d.day_type ? DAY_META[d.day_type] : null;
                  return (
                    <button
                      key={d.date}
                      onClick={() => { setSelected(d.date); setPendingType(null); setEditVal(""); setShowEarnings(false); setShowWeek(false); }}
                      className="rounded-lg py-1.5 flex flex-col items-center justify-start min-h-[52px] transition-transform hover:scale-105"
                      style={{
                        background: inWeekOff ? "var(--pr-tint-teal)" : d.date > data.today ? "var(--pr-bg-alt)" : COLOR.bg,
                        border: isSel ? `2px solid ${COLOR.ink}` : isToday ? `2px solid ${COLOR.gold}` : `1px solid ${COLOR.line}`,
                      }}
                      data-testid={`cal-day-${d.date}`}
                    >
                      <span className="text-xs font-bold" style={{ color: d.date > data.today ? COLOR.muted : COLOR.ink }}>
                        {Number(d.date.slice(8, 10))}
                      </span>
                      <span className="text-[11px] leading-none mt-0.5">
                        {meta ? meta.emoji : d.earned ? <span className="font-bold" style={{ color: COLOR.green, fontSize: 9 }}>$</span> : "\u00A0"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mb-5" style={{ color: COLOR.ink2 }} data-testid="cal-legend">
              <span>😌 day off</span>
              <span>🤒 sick</span>
              <span>🌴 PTO</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-tint-teal)" }} /> PTO week</span>
              <span className="font-bold" style={{ color: COLOR.green }}>$ earnings logged</span>
            </div>

            {/* Selected day panel */}
            {sel && (
              <div className="rounded-2xl p-5 lg:p-6 mb-5 pr-card" data-testid="day-panel">
                <p className="font-bold text-base mb-0.5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  {fullDate(sel.date)}
                </p>
                <p className="text-xs mb-3" style={{ color: COLOR.muted }}>
                  {sel.earned ? <>💵 {fmt(sel.earned)} logged · </> : null}
                  {selIsFuture ? "Up ahead, schedule it now, we'll remember." : "Tag it however the day really went."}
                  {sel.day_type && (
                    <> Currently: <b style={{ color: COLOR.ink }}>{DAY_META[sel.day_type].emoji} {DAY_META[sel.day_type].label}{sel.paid_from_bank ? ` (paid, ${fmt(sel.amount_covered)})` : ""}</b></>
                  )}
                </p>

                {!selIsFuture && !showEarnings && (
                  <button
                    type="button"
                    onClick={() => setShowEarnings(true)}
                    className="text-[11px] font-semibold underline mb-3"
                    style={{ color: COLOR.muted }}
                    data-testid="day-earnings-toggle"
                  >
                    Fix this day&rsquo;s earnings
                  </button>
                )}

                {!selIsFuture && showEarnings && (
                  <div className="rounded-xl p-3 border mb-3" style={{ borderColor: COLOR.line, background: "var(--pr-bg)" }} data-testid="day-earnings-edit">
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: COLOR.muted }}>
                      Fix this day&rsquo;s earnings
                    </p>
                    <div className="flex flex-col gap-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: COLOR.muted }}>$</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          placeholder={sel.earned != null ? String(sel.earned) : "0.00"}
                          disabled={busy}
                          className="w-full pl-7 pr-2 py-2 rounded-lg border text-sm font-semibold focus:outline-none"
                          style={{ borderColor: COLOR.line, background: "var(--pr-card)", color: COLOR.ink }}
                          data-testid="day-earnings-input"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => { const v = parseFloat(editVal); if (!Number.isNaN(v) && v >= 0) setDayEarnings(v); }}
                          disabled={busy || editVal === "" || Number.isNaN(parseFloat(editVal)) || parseFloat(editVal) < 0}
                          className="flex-1 px-4 py-2 rounded-full text-sm font-bold disabled:opacity-40"
                          style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                          data-testid="day-earnings-update-btn"
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          onClick={() => setDayEarnings(0)}
                          disabled={busy}
                          className="px-4 py-2 rounded-full text-sm font-bold border disabled:opacity-40"
                          style={{ borderColor: COLOR.red, color: COLOR.red, background: "var(--pr-card)" }}
                          data-testid="day-earnings-clear-btn"
                        >
                          Clear $
                        </button>
                      </div>
                    </div>
                    <p className="text-[11px] mt-1.5" style={{ color: COLOR.muted }}>
                      Sets this day&rsquo;s total, replacing what&rsquo;s logged in the app <b>and</b> your sheet (no double-counting). Works even for days you logged straight in the sheet.
                    </p>
                  </div>
                )}

                {!pendingType ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => tagDay("day_off")}
                      disabled={busy}
                      className="px-4 py-2 rounded-full text-sm font-bold border disabled:opacity-40"
                      style={{ borderColor: COLOR.line, background: sel.day_type === "day_off" ? COLOR.ink : "#fff", color: sel.day_type === "day_off" ? "#fff" : COLOR.ink }}
                      data-testid="tag-dayoff-btn"
                    >
                      😌 Day off
                    </button>
                    <button
                      onClick={() => setPendingType("sick")}
                      disabled={busy}
                      className="px-4 py-2 rounded-full text-sm font-bold border disabled:opacity-40"
                      style={{ borderColor: COLOR.line, background: sel.day_type === "sick" ? COLOR.ink : "#fff", color: sel.day_type === "sick" ? "#fff" : COLOR.ink }}
                      data-testid="tag-sick-btn"
                    >
                      🤒 Sick day
                    </button>
                    <button
                      onClick={() => setPendingType("pto")}
                      disabled={busy}
                      className="px-4 py-2 rounded-full text-sm font-bold border disabled:opacity-40"
                      style={{ borderColor: COLOR.line, background: sel.day_type === "pto" ? COLOR.teal : "#fff", color: sel.day_type === "pto" ? "#fff" : COLOR.teal }}
                      data-testid="tag-pto-btn"
                    >
                      🌴 PTO day
                    </button>
                    {sel.day_type && (
                      <button
                        onClick={() => tagDay("none")}
                        disabled={busy}
                        className="px-4 py-2 rounded-full text-sm font-semibold underline disabled:opacity-40"
                        style={{ color: COLOR.muted }}
                        data-testid="tag-clear-btn"
                      >
                        Clear tag
                      </button>
                    )}
                  </div>
                ) : (
                  <div data-testid="paid-choice-panel">
                    <p className="text-sm font-bold mb-2" style={{ color: COLOR.ink }}>
                      {DAY_META[pendingType].emoji} {DAY_META[pendingType].label}, how should we log it?
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => tagDay(pendingType, false)}
                        disabled={busy}
                        className="w-full text-left rounded-xl p-3 border disabled:opacity-40"
                        style={{ borderColor: COLOR.line, background: "var(--pr-card)" }}
                        data-testid="paid-choice-unpaid"
                      >
                        <p className="text-sm font-bold" style={{ color: COLOR.ink }}>Just track it, unpaid</p>
                        <p className="text-[11px]" style={{ color: COLOR.muted }}>
                          {pendingType === "sick" ? "It's okay to just be sick. Zero-income day, bank untouched." : "A rest day on the record, bank untouched."}
                        </p>
                      </button>
                      <button
                        onClick={() => canAffordPaid && tagDay(pendingType, true)}
                        disabled={busy || !canAffordPaid}
                        className="w-full text-left rounded-xl p-3.5 border disabled:opacity-50"
                        style={{ borderColor: "var(--pr-card-line)", background: COLOR.tealSoft }}
                        data-testid="paid-choice-bank"
                      >
                        <p className="text-sm font-bold" style={{ color: COLOR.teal }}>
                          Cover it from my PTO bank{data.daily_target != null ? ` (−${fmt(data.daily_target)})` : ""}
                        </p>
                        <p className="text-[11px]" style={{ color: COLOR.ink2 }}>
                          {canAffordPaid
                            ? "You did the work to save it, pay yourself. Counts toward this relay's target."
                            : data.daily_target == null
                              ? "Run the Relay Calculator first so we know your daily target."
                              : `Bank has ${fmt(data.bank?.balance)}. Not quite enough yet. Log your PTO set-asides to grow it.`}
                        </p>
                      </button>
                      <button
                        onClick={() => setPendingType(null)}
                        className="text-xs underline"
                        style={{ color: COLOR.muted }}
                        data-testid="paid-choice-cancel"
                      >
                        Never mind
                      </button>
                    </div>
                  </div>
                )}

                {/* Whole-week PTO, tucked away until it's needed */}
                <div className="mt-4 pt-3 border-t" style={{ borderColor: COLOR.line }}>
                  {!showWeek && !selWeekOff ? (
                    <button
                      type="button"
                      onClick={() => setShowWeek(true)}
                      className="text-[11px] font-semibold underline"
                      style={{ color: COLOR.muted }}
                      data-testid="week-off-reveal"
                    >
                      🌴 Taking this whole week off?
                    </button>
                  ) : (
                    <>
                  <button
                    onClick={() => (selWeekOff ? toggleWeekOff(false) : setWeekPending(true))}
                    disabled={busy}
                    className="w-full py-2.5 rounded-full font-bold text-sm disabled:opacity-40 transition-transform hover:scale-[1.01]"
                    style={selWeekOff ? { background: "var(--pr-card)", color: COLOR.teal, border: `2px solid ${COLOR.teal}` } : { background: "var(--pr-teal-surface)", color: "#fff" }}
                    data-testid="week-off-toggle"
                  >
                    {selWeekOff ? "Unmark this PTO week" : "🌴 Mark this whole week as PTO"}
                  </button>
                  {selWeekOff && selWeekDetail && (
                    <p className="text-[11px] mt-1.5 text-center" style={{ color: COLOR.teal }} data-testid="week-off-status">
                      {selWeekDetail.paid_from_bank ? `Paid week · ${fmt(selWeekDetail.amount_covered)} drawn from your PTO bank` : "Unpaid week · nothing drawn from your PTO bank"}
                    </p>
                  )}
                  {weekPending && !selWeekOff && (
                    <div className="mt-3 rounded-2xl p-4 border" style={{ background: "var(--pr-bg)", borderColor: COLOR.line }} data-testid="week-paid-choice">
                      <p className="text-xs font-bold mb-2" style={{ color: COLOR.ink }}>Paid or unpaid week?</p>
                      <button
                        onClick={() => toggleWeekOff(true, false)}
                        disabled={busy}
                        className="w-full text-left rounded-xl p-3 border mb-2 disabled:opacity-40"
                        style={{ background: "var(--pr-card)", borderColor: COLOR.line }}
                        data-testid="week-paid-choice-unpaid"
                      >
                        <p className="text-sm font-bold" style={{ color: COLOR.ink }}>Unpaid week</p>
                        <p className="text-[11px]" style={{ color: COLOR.muted }}>On the record, streak stays safe. Nothing leaves your PTO bank.</p>
                      </button>
                      <button
                        onClick={() => toggleWeekOff(true, true)}
                        disabled={busy || !canAffordWeek}
                        className="w-full text-left rounded-xl p-3 border disabled:opacity-40"
                        style={{ background: "var(--pr-card)", borderColor: COLOR.teal }}
                        data-testid="week-paid-choice-bank"
                      >
                        <p className="text-sm font-bold" style={{ color: COLOR.teal }}>Cover it from my PTO bank</p>
                        <p className="text-[11px]" style={{ color: COLOR.muted }}>
                          {weekCost == null
                            ? "Run your Relay Calculator first so we know your daily target."
                            : canAffordWeek
                              ? `Draws ${fmt(weekCost)} (${daysPerWeek} days × ${fmt(data.daily_target)}), counts as PTO used on your sheet.`
                              : `Bank has ${fmt(data.bank?.balance)}. Not enough for a ${fmt(weekCost)} week yet.`}
                        </p>
                      </button>
                      <button onClick={() => setWeekPending(false)} className="text-xs underline mt-2" style={{ color: COLOR.muted }} data-testid="week-paid-choice-cancel">
                        Never mind
                      </button>
                    </div>
                  )}
                  <p className="text-[11px] mt-1.5 text-center leading-relaxed" style={{ color: COLOR.muted }}>
                    PTO weeks show teal on your scoreboard. Never red, never a broken streak. ({shortDate(sel.week_start)} week)
                  </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {!sel && (
              <p className="text-xs text-center mb-5" style={{ color: COLOR.muted }} data-testid="timeoff-hint">
                Tap any day, past or future, to tag it.
              </p>
            )}

            <p className="text-[11px] text-center leading-relaxed px-4 py-6" style={{ color: COLOR.muted }}>
              Day tags live in Payday Relay; paid PTO draws sync to the PTO-used line on your sheet, so every payout math stays honest.
              Payday Relay is an educational tool. Not financial, tax, or investment advice.{" "}
              <Link to="/terms" className="underline">Terms</Link>
            </p>
          </div>
        </main>
      </div>
      <div className="h-20 lg:hidden" />
      <CaddyBottomNav active="more" sprint={data?.entitlements?.mode === "sprint"} />
    </div>
  );
}
